/**
 * «Le mie stanze» — la parte senza disegno, condivisa fra la home e lo Spotlight.
 *
 * Contratto: `maefwk10/docs/contract/BE-TO-FE-MAESPACE-IMPLEMENTAZIONE-v1.md` §7. Nessun permesso
 * particolare: ognuno vede solo le sue. Si legge solo a modulo acceso — spento, la rotta risponde
 * `403 MODULE_DISABLED` e il contratto dice «non mostrare niente».
 */
import { useQuery } from '@tanstack/react-query';
import { openExternalUrl } from '@/shared/lib/nativePlatform';
import { notifyWarn } from '@/shared/lib/notify';
import { fetchMyRooms, maespaceQueryKeys, type MyRoom, type RoomOwnerType } from './api';

/** Che cosa è una stanza, detto come lo dice lo schermo («progetto», non «commessa»). */
export const ROOM_KIND_LABEL: Record<RoomOwnerType, string> = {
  project: 'Progetto',
  task: 'Ticket',
  'planning-entry': 'Riunione',
  'meeting-room-booking': 'Sala',
};

/** L'etichetta del tipo; un tipo che il BE aggiunge senza passare di qui si mostra com'è. */
export function roomKindLabel(ownerType: string): string {
  return (ROOM_KIND_LABEL as Record<string, string>)[ownerType] ?? ownerType;
}

/** Il nome da mostrare: quello della stanza, poi quello del progetto, poi un ripiego che lo dice. */
export function roomTitle(room: Pick<MyRoom, 'name' | 'project'>): string {
  return room.name?.trim() || room.project?.name?.trim() || 'Stanza senza nome';
}

/** Quante stanze la home mostra prima di «mostra tutte». */
export const HOME_ROOMS_LIMIT = 6;

/** Le stanze da disegnare e quante ne restano fuori. */
export function trimRooms<T>(rooms: readonly T[], showAll: boolean, limit = HOME_ROOMS_LIMIT): { visible: T[]; hidden: number } {
  if (showAll || rooms.length <= limit) return { visible: [...rooms], hidden: 0 };
  return { visible: rooms.slice(0, limit), hidden: rooms.length - limit };
}

/**
 * Apre un indirizzo esterno — la stanza, il link della riunione — dalla porta unica, che nell'app
 * passa al browser di sistema invece di caricarlo dentro la WebView. Se il browser blocca la
 * finestra lo dice, invece di non fare niente.
 */
export function openExternalOrWarn(url: string): void {
  if (!openExternalUrl(url)) {
    notifyWarn('Il browser ha bloccato la finestra: consenti le finestre a comparsa per questo sito e riprova.', 'Apertura link');
  }
}

/** Le stanze dell'utente: una query sola, la stessa per la home e per lo Spotlight. */
export function useMyRooms(enabled: boolean) {
  return useQuery({
    queryKey: maespaceQueryKeys.myRooms(),
    queryFn: fetchMyRooms,
    enabled,
    staleTime: 5 * 60_000,
  });
}
